import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { getFullImageUrl } from '../../utils/imageUtils';
import MenuItemModal from './MenuItemModal';
import './MenuManagement.css';

const ProductManagement = ({ store, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const [editingProduct, setEditingProduct] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [variationTemplates, setVariationTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const getBaseUrl = () => {
    return (user?.role === 'store_owner' || user?.role === 'store_user')
      ? `${API_URL}/store-owner/stores/${store._id}`
      : `${API_URL}/admin/stores/${store._id}`;
  };

  const getTemplatesUrl = () => {
    return (user?.role === 'store_owner' || user?.role === 'store_user')
      ? `${API_URL}/store-owner/variation-templates`
      : `${API_URL}/admin/variation-templates`;
  };

  const fetchVariationTemplates = React.useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const url = getTemplatesUrl();
      const res = await axios.get(url, { headers: { Accept: 'application/json' } });
      setVariationTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load variation templates:', err?.response?.status, err?.response?.data);
      showError(err?.response?.data?.message || 'Could not load variation templates');
      // Keep previous templates on error so modal still works if a prior load succeeded
    } finally {
      setTemplatesLoading(false);
    }
  }, [user?.role, showError]);

  // Load templates on mount and when user/role is available
  useEffect(() => {
    if (user?.role) fetchVariationTemplates();
  }, [user?.role, fetchVariationTemplates]);

  // Refetch templates when opening add/edit product modal so they're always fresh and auth is ready
  useEffect(() => {
    if (editingProduct != null && user?.role) {
      fetchVariationTemplates();
    }
  }, [editingProduct, user?.role, fetchVariationTemplates]);

  // Default categories for products (can be customized)
  const defaultCategories = ['Electronics', 'Groceries', 'Clothing', 'Home & Garden', 'Beauty & Health', 'Sports', 'Other'];
  const hiddenDefaults = store.hiddenProductCategories || [];

  // Visible default categories + custom categories, remove duplicates
  const allCategories = [...new Set([
    ...defaultCategories.filter(c => !hiddenDefaults.includes(c)),
    ...(store.productCategories || [])
  ])];

  const handleDeleteProduct = async (productId) => {
    if (!productId) {
      showError('Cannot delete: product id missing');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const baseUrl = getBaseUrl();
      await axios.delete(`${baseUrl}/products/${productId}`);
      onUpdate();
    } catch (error) {
      console.error('Error deleting product:', error);
      const message = error.response?.data?.message || error.message || 'Error deleting product';
      showError(message);
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      const baseUrl = getBaseUrl();
      if (editingProduct && editingProduct._id) {
        await axios.patch(
          `${baseUrl}/products/${editingProduct._id}`,
          productData
        );
      } else {
        await axios.post(
          `${baseUrl}/products`,
          productData
        );
      }
      onUpdate();
      setEditingProduct(null);
    } catch (error) {
      console.error('Error saving product:', error);
      showError('Error saving product');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    const category = newCategory.trim();
    if (allCategories.includes(category)) {
      showError('Category already exists');
      return;
    }

    try {
      const baseUrl = getBaseUrl();
      const updatedCategories = [...(store.productCategories || []), category];
      await axios.patch(baseUrl, {
        productCategories: updatedCategories
      });
      setNewCategory('');
      setShowCategoryInput(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding category:', error);
      showError('Error adding category');
    }
  };

  const handleRemoveCategory = async (category) => {
    const isDefault = defaultCategories.includes(category);
    const productsInCategory = (store.products || []).filter(product => product.category === category);
    if (productsInCategory.length > 0) {
      const confirmMessage = `Warning: There are ${productsInCategory.length} product(s) in the category "${category}".\n\nRemoving this category will not delete the products, but they will need to be assigned to a different category.\n\nDo you still want to remove this category?`;
      if (!window.confirm(confirmMessage)) return;
    } else {
      if (!window.confirm(`Are you sure you want to remove the category "${category}"?`)) return;
    }

    try {
      const baseUrl = getBaseUrl();
      if (isDefault) {
        const updatedHidden = [...(store.hiddenProductCategories || []), category];
        await axios.patch(baseUrl, { hiddenProductCategories: updatedHidden });
      } else {
        const updatedCategories = (store.productCategories || []).filter(cat => cat !== category);
        await axios.patch(baseUrl, { productCategories: updatedCategories });
      }
      onUpdate();
    } catch (error) {
      console.error('Error removing category:', error);
      showError('Error removing category');
    }
  };

  // Group products by category
  const productsByCategory = {};
  (store.products || []).forEach(product => {
    const category = product.category || 'Other';
    if (!productsByCategory[category]) {
      productsByCategory[category] = [];
    }
    productsByCategory[category].push(product);
  });

  return (
    <div className="menu-management-overlay">
      <div className="menu-management-content">
        <div className="menu-management-header">
          <h2>Manage Products - {store.name}</h2>
          <div className="menu-management-header-actions">
            <label className="track-stock-toggle">
              <input
                type="checkbox"
                checked={!!store.settings?.trackStock}
                onChange={async (e) => {
                  try {
                    const baseUrl = getBaseUrl();
                    await axios.patch(baseUrl, { settings: { ...(store.settings || {}), trackStock: e.target.checked } });
                    onUpdate();
                  } catch (err) {
                    showError(err?.response?.data?.message || 'Failed to update setting');
                  }
                }}
              />
              <span>Track stock</span>
            </label>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="menu-management-body">
          {/* Category Management Section */}
          <div className="category-management-section">
            <div className="section-header">
              <h3>Product Categories</h3>
              <button 
                className="btn-add-category"
                onClick={() => setShowCategoryInput(!showCategoryInput)}
              >
                <i className="fas fa-plus"></i>
                Add Category
              </button>
            </div>
            
            {showCategoryInput && (
              <div className="add-category-form">
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <button onClick={handleAddCategory} className="btn-save-category">
                  <i className="fas fa-check"></i>
                </button>
                <button onClick={() => { setShowCategoryInput(false); setNewCategory(''); }} className="btn-cancel-category">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            <div className="categories-list">
              {allCategories.map((category, index) => {
                const productsInCategory = (store.products || []).filter(product => product.category === category).length;
                const isDefault = defaultCategories.includes(category);
                return (
                  <div key={index} className={`category-tag ${isDefault ? 'default-category' : 'custom-category'}`}>
                    <div className="category-info">
                      <span className="category-name">{category}</span>
                      <span className="category-item-count">({productsInCategory} product{productsInCategory !== 1 ? 's' : ''})</span>
                    </div>
                    <button 
                      onClick={() => handleRemoveCategory(category)}
                      className="btn-remove-category"
                      title={`Remove category "${category}"${productsInCategory > 0 ? ` (${productsInCategory} products will need reassignment)` : ''}`}
                      aria-label={`Remove category ${category}`}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Products Section */}
          <div className="menu-items-section">
            <div className="section-header">
              <h3>Products</h3>
              <button 
                className="btn-add-item"
                onClick={() => setEditingProduct({})}
              >
                <i className="fas fa-plus"></i>
                Add Product
              </button>
            </div>

            {Object.keys(productsByCategory).length === 0 ? (
              <div className="empty-menu-state">
                <i className="fas fa-box"></i>
                <p>No products yet. Click 'Add Product' to get started.</p>
              </div>
            ) : (
              <div className="menu-items-list">
                {Object.entries(productsByCategory).map(([category, products]) => (
                  <div key={category} className="menu-category-group">
                    <h4 className="category-title">{category}</h4>
                    <div className="menu-items-grid">
                      {products.map(product => (
                        <div key={product._id} className="menu-item-card">
                          <div className="menu-item-image">
                            {product.image ? (
                              <img src={getFullImageUrl(product.image)} alt={product.name} />
                            ) : (
                              <div className="no-image-placeholder">
                                <i className="fas fa-image"></i>
                              </div>
                            )}
                          </div>
                          <div className="menu-item-info">
                            <h5>{product.name}</h5>
                            <p className="menu-item-description">{product.description || 'No description'}</p>
                            <p className="menu-item-price">€{product.price?.toFixed(2) || '0.00'}</p>
                            {store.settings?.trackStock && (
                              <p className="menu-item-stock">Stock: {product.stockQty != null ? product.stockQty : '—'}</p>
                            )}
                            {(product.variations?.length > 0 || product.ingredients?.length > 0) && (
                              <span className="variations-badge">
                                {product.variations?.length > 0 && <><i className="fas fa-list"></i> {product.variations.length} variation{product.variations.length !== 1 ? 's' : ''}</>}
                                {product.ingredients?.length > 0 && <><i className="fas fa-leaf"></i> {product.ingredients.length} ingredient{product.ingredients.length !== 1 ? 's' : ''}</>}
                              </span>
                            )}
                          </div>
                          <div className="menu-item-actions">
                            <button 
                              onClick={() => setEditingProduct(product)}
                              className="btn-edit-item"
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              onClick={() => handleDeleteProduct(product._id)}
                              className="btn-delete-item"
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="menu-management-footer">
          <button onClick={onClose} className="btn-close">Close</button>
        </div>
      </div>

      {editingProduct !== null && (
        <MenuItemModal
          isOpen={editingProduct !== null}
          onClose={() => setEditingProduct(null)}
          onSave={handleSaveProduct}
          item={editingProduct}
          isProduct={true}
          storeId={store._id}
          categories={allCategories}
          variationTemplates={variationTemplates}
          variationTemplatesLoading={templatesLoading}
        />
      )}
    </div>
  );
};

export default ProductManagement;
